import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Lock body scroll when mobile menu open — NEVER use position:fixed on body on mobile!
  useEffect(() => {
    if (mobileOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none";
      // Preserve scroll position so the page doesn't jump
      document.body.style.top = `-${scrollY}px`;
    } else {
      const top = parseInt(document.body.style.top || "0", 10);
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
      document.body.style.top = "";
      if (top) {
        window.scrollTo(0, Math.abs(top));
      }
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = "";
      document.body.style.top = "";
    };
  }, [mobileOpen]);

  const openMobile = useCallback(() => setMobileOpen(true), []);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  const isHome = location.pathname === "/";
  const navDark = !scrolled && isHome;

  return (
    <>
      {/* ===== NAVBAR ===== */}
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          navDark
            ? "bg-gradient-to-b from-black/25 to-transparent"
            : "bg-background-50/95 backdrop-blur-md"
        }`}
      >
        <div className="w-full px-4 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2 group cursor-pointer flex-shrink-0"
            >
              <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center bg-accent-500 rounded-lg">
                <i className="ri-sailboat-fill text-background-50 text-lg"></i>
              </div>
              <span
                className={`font-heading text-lg md:text-xl font-semibold whitespace-nowrap ${
                  navDark ? "text-background-50" : "text-foreground-900"
                }`}
              >
                Kakobuy Yachts
              </span>
            </Link>

            {/* Desktop Center Nav */}
            <div className="hidden md:flex items-center gap-6">
              <Link
                to="/"
                className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  navDark
                    ? "text-background-50/90 hover:text-background-50"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {t("nav.home")}
              </Link>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1.5 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                    navDark
                      ? "text-background-50/90 hover:text-background-50"
                      : "text-foreground-700 hover:text-foreground-950"
                  }`}
                >
                  <span>{t("nav.categories")}</span>
                  <i
                    className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                      dropdownOpen ? "rotate-180" : ""
                    }`}
                  ></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-background-50 rounded-xl shadow-lg border border-background-200/80 py-2 w-[640px] z-50 max-h-[60vh] overflow-y-auto">
                    <div className="grid grid-cols-2 gap-1 px-2">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 group-hover:bg-secondary-200 transition-colors flex-shrink-0">
                            <i className={`${cat.icon} text-sm`}></i>
                          </div>
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">
                              {cat.name}
                            </span>
                            <span className="text-xs text-foreground-500 ml-2">
                              {cat.namePl}
                            </span>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link
                to="/artykul/jak-korzystac-z-kakobuy-spreadsheet"
                className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  navDark
                    ? "text-background-50/90 hover:text-background-50"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {t("nav.guide")}
              </Link>

              <Link
                to="/#artykuly"
                className={`text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  navDark
                    ? "text-background-50/90 hover:text-background-50"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                {t("nav.articles")}
              </Link>
            </div>

            {/* Right: CTA + Mobile Hamburger */}
            <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer whitespace-nowrap border bg-background-50/15 border-background-50/30 text-background-50 hover:bg-background-50/25"
              >
                {t("nav.kakobuySpreadsheet")}
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-full transition-all cursor-pointer whitespace-nowrap bg-accent-500 text-background-50 hover:bg-accent-600"
              >
                {t("nav.buyItNow")}
              </a>

              {/* Mobile hamburger — always ri-menu-line, never changes to X */}
              <button
                onClick={openMobile}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg cursor-pointer -mr-1"
                aria-label="Otwórz menu"
              >
                <i
                  className={`text-xl ${
                    navDark ? "text-background-50" : "text-foreground-800"
                  } ri-menu-line`}
                ></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ===== MOBILE OVERLAY ===== */}
      <div
        className={`md:hidden fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeMobile}
        aria-hidden="true"
      />

      {/* ===== MOBILE SIDE PANEL ===== */}
      <div
        className={`md:hidden fixed top-0 left-0 h-full z-50 w-[85vw] max-w-[380px] bg-background-50 shadow-2xl transition-transform duration-300 ease-out overflow-y-auto overscroll-contain ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Panel header */}
        <div className="sticky top-0 z-10 bg-background-50 flex items-center justify-between px-5 py-4 border-b border-background-200/60">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-accent-500 rounded-lg">
              <i className="ri-sailboat-fill text-background-50 text-lg"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-foreground-900 whitespace-nowrap">
              Kakobuy Yachts
            </span>
          </div>
          <button
            onClick={closeMobile}
            className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
            aria-label="Zamknij menu"
          >
            <i className="ri-close-line text-xl text-foreground-700"></i>
          </button>
        </div>

        {/* Panel content */}
        <div className="px-5 py-6 space-y-6">
          {/* Quick Links */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest text-foreground-400 mb-4 uppercase">
              {t("nav.navigation")}
            </h4>
            <div className="space-y-1">
              <Link
                to="/"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-900 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-700">
                  <i className="ri-home-4-line"></i>
                </div>
                {t("nav.home")}
              </Link>
              <Link
                to="/artykul/jak-korzystac-z-kakobuy-spreadsheet"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-900 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-book-open-line"></i>
                </div>
                {t("nav.guide")}
              </Link>
              <Link
                to="/#artykuly"
                onClick={closeMobile}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-900 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-article-line"></i>
                </div>
                {t("nav.articles")}
              </Link>
            </div>
          </div>

          {/* Categories */}
          <div>
            <h4 className="text-xs font-semibold tracking-widest text-foreground-400 mb-4 uppercase">
              {t("nav.categories")}
            </h4>
            <div className="space-y-1">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={closeMobile}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-foreground-700 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 flex-shrink-0">
                    <i className={`${cat.icon} text-sm`}></i>
                  </div>
                  <span className="whitespace-nowrap">{cat.name}</span>
                  <span className="text-xs text-foreground-400 ml-auto">{cat.namePl}</span>
                </a>
              ))}
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="pt-4 border-t border-background-200/60 space-y-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 active:bg-primary-700 transition-all cursor-pointer"
            >
              {t("nav.kakobuySpreadsheet")}
              <i className="ri-arrow-right-up-line"></i>
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              onClick={closeMobile}
              className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full text-sm font-semibold bg-accent-500 text-background-50 hover:bg-accent-600 active:bg-accent-700 transition-all cursor-pointer"
            >
              {t("nav.buyItNow")}
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </div>
    </>
  );
}